import { FC, useCallback } from "react";
import { observer } from "mobx-react-lite";
import { StepRenderProps } from "@/components/onboarding/components/welcome-wizard/welcome-wizard.types";
import { useOnboardingSystemPermissionsHandlers } from "@/components/onboarding/components/welcome-wizard/shared";
import { OnboardingVoicePermissionsScreen } from "@/components/onboarding/components/welcome-wizard/steps/system-permission-voice/components";
import { TrackedEvent, trackEvent } from "@/domains/metrics";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";

export const SystemPermissionsVoicePermissionsStep: FC<StepRenderProps> = observer(
  ({ onNavToStep, onClose }) => {
    const {
      resolveConfigurationStatus,
      handleEnablePermission,
      handleOpenPermissionPane,
      restartRequired,
    } = useOnboardingSystemPermissionsHandlers();

    const handleNext = useCallback(() => {
      trackEvent(TrackedEvent.VoiceSetupSubmit, {
        enabled: true,
      });

      if (restartRequired) {
        onNavToStep?.("system-permissions-restart-if-required");
      } else {
        /**
         * If we don't need to restart, then we're all done with the system-permissions flow.
         */
        onClose?.();
      }
    }, [onClose, onNavToStep, restartRequired]);

    const handleSkip = () => {
      trackEvent(TrackedEvent.VoiceSetupSubmit, {
        enabled: false,
      });

      /**
       * If the user skips the voice permissions step, we're all done with the system-permissions flow.
       */
      onClose?.();
    };

    useEffectOnMount(() => {
      trackEvent(TrackedEvent.VoiceSetupView);
    });

    return (
      <OnboardingVoicePermissionsScreen
        onNext={handleNext}
        onSkip={handleSkip}
        restartRequired={restartRequired}
        resolveConfigurationStatus={resolveConfigurationStatus}
        handleEnablePermission={handleEnablePermission}
        handleOpenPermissionPane={handleOpenPermissionPane}
      />
    );
  }
);
